/**
 * Debug / utility nodes.
 */
import type { NodeBehaviour } from '../types';
import { flowIn, flowOut, input, output } from '../node/NodeRegistry';

export const DebugLog: NodeBehaviour = {
  id: 'debug.log',
  name: 'Print',
  category: 'Debug',
  color: '#95a5a6',
  icon: 'Terminal',
  description: 'Prints a message to the console.',
  keywords: ['log', 'print', 'console', 'trace'],
  inputs: [
    flowIn(),
    input('msg', 'Message', 'string', 'Hello'),
  ],
  outputs: [
    flowOut(),
  ],
  execute(ctx) {
    console.log('[Redprint]', ctx.getInput<string>('msg'));
  },
  compile(ctx) {
    ctx.emit('debug:log', [ctx.resolveInput('msg')], 'string', true);
  },
};

export const DebugLogF32: NodeBehaviour = {
  id: 'debug.logF32',
  name: 'Print Number',
  category: 'Debug',
  color: '#95a5a6',
  icon: 'Terminal',
  description: 'Prints a float value to the console.',
  keywords: ['log', 'print', 'number'],
  inputs: [
    flowIn(),
    input('label', 'Label', 'string', 'value'),
    input('value', 'Value', 'f32', 0),
  ],
  outputs: [
    flowOut(),
  ],
  execute(ctx) {
    console.log(`[Redprint] ${ctx.getInput<string>('label')}: ${ctx.getInput<number>('value')}`);
  },
  compile(ctx) {
    // Generate: `${label}: ${value}`
    ctx.emit('debug:log', [
      `${ctx.resolveInput('label')} + ": " + ${ctx.resolveInput('value')}`,
    ], 'string', true);
  },
};

export const ToString: NodeBehaviour = {
  id: 'debug.toString',
  name: 'To String',
  category: 'Debug',
  isPure: true,
  color: '#95a5a6',
  icon: 'Type',
  description: 'Converts any value to a string.',
  keywords: ['convert', 'cast', 'format'],
  inputs: [
    input('value', 'Value', 'any'),
  ],
  outputs: [
    output('result', 'String', 'string'),
  ],
  execute(ctx) {
    const v = ctx.getInput('value');
    ctx.setOutput('result', String(v ?? ''));
  },
  compile(ctx) {
    return ctx.emit('op:to_string', [ctx.resolveInput('value')], 'string');
  },
};

export const Concat: NodeBehaviour = {
  id: 'debug.concat',
  name: 'Concat Strings',
  category: 'Debug',
  isPure: true,
  color: '#95a5a6',
  icon: 'Link',
  description: 'Concatenates two strings.',
  keywords: ['join', 'append', 'merge', 'combine'],
  inputs: [
    input('a', 'A', 'string', ''),
    input('b', 'B', 'string', ''),
  ],
  outputs: [
    output('result', 'Result', 'string'),
  ],
  execute(ctx) {
    ctx.setOutput('result', `${ctx.getInput<string>('a') ?? ''}${ctx.getInput<string>('b') ?? ''}`);
  },
  compile(ctx) {
    return ctx.emit('op:concat', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'string');
  },
};

export const GetTime: NodeBehaviour = {
  id: 'debug.time',
  name: 'Time',
  category: 'Debug',
  isPure: true,
  color: '#95a5a6',
  icon: 'Clock',
  description: 'Gets the current elapsed time in seconds.',
  keywords: ['elapsed', 'clock', 'seconds'],
  inputs: [],
  outputs: [
    output('time', 'Time', 'f32'),
  ],
  execute(ctx) {
    ctx.setOutput('time', ctx.time);
  },
  compile(ctx) {
    return ctx.emit('event_param:time', [], 'f32');
  },
};

// ── Variable nodes (Get / Set) ──────────────────────────────────

export const GetVariable: NodeBehaviour = {
  id: 'variables.get',
  name: 'Get Variable',
  category: 'Variables',
  isPure: true,
  color: '#27ae60',
  icon: 'Download',
  description: 'Reads the value of a graph variable.',
  keywords: ['read', 'load', 'fetch'],
  inputs: [
    input('name', 'Name', 'string', ''),
  ],
  outputs: [
    output('value', 'Value', 'any'),
  ],
  execute(ctx) {
    const name = ctx.getInput<string>('name') ?? '';
    ctx.setOutput('value', ctx.getVariable(name));
  },
  compile(ctx) {
    const nameRef = ctx.resolveInput('name');
    return ctx.emit('var_get', [nameRef], 'any');
  },
};

export const SetVariable: NodeBehaviour = {
  id: 'variables.set',
  name: 'Set Variable',
  category: 'Variables',
  color: '#27ae60',
  icon: 'Upload',
  description: 'Writes a value to a graph variable.',
  keywords: ['write', 'store', 'assign'],
  inputs: [
    flowIn(),
    input('name', 'Name', 'string', ''),
    input('value', 'Value', 'any'),
  ],
  outputs: [
    flowOut(),
    output('value', 'Value', 'any'),
  ],
  execute(ctx) {
    const name = ctx.getInput<string>('name') ?? '';
    const val = ctx.getInput('value');
    ctx.setVariable(name, val);
    ctx.setOutput('value', val);
  },
  compile(ctx) {
    const nameRef = ctx.resolveInput('name');
    const valueRef = ctx.resolveInput('value');
    ctx.emit('var_set', [nameRef, valueRef], 'any', true);
    return valueRef;
  },
};

export const DEBUG_NODES: readonly NodeBehaviour[] = [
  DebugLog, DebugLogF32, ToString, Concat, GetTime,
  GetVariable, SetVariable,
];
